import { DecimalPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
  CommunityProfileWidgetConfig,
  MULTI_FAMILY_VERIFIABLE_PROPERTIES,
} from '@trp/community/interfaces';
import {
  AdditionalServicesPipe,
  ContainersPipe,
  MethodsPipe,
} from '@trp/community/shared/ui';
import { MultiFamilyProgram } from '@trp/nationaldb/interfaces';
import { CommunityWidgetInfoUtilService } from '../services';


const multiFamilyPrograms = (
  translateService: TranslateService,
  decimalPipe: DecimalPipe,
  containersPipe: ContainersPipe,
  utilService: CommunityWidgetInfoUtilService,
  additionalServicePipe: AdditionalServicesPipe,
): CommunityProfileWidgetConfig['multiFamilyPrograms'] => ({
  section: 'multiFamilyPrograms',
  title: 'widgets.multiFamilyProgram.title',
  maxColumns: 3,
  propertiesToVerify: [...MULTI_FAMILY_VERIFIABLE_PROPERTIES],
  widgets: [
    {
      section: 'multiFamilyPrograms',
      infoType: 'programUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.multiFamilyProgram.programUrl',
      icon: 'global:search',
      infoText: 'requestForm.questions.general.onlineToolForAcceptedMaterialsInfo',
      availableValueFormat: 'html',
      ifInfoAvailable: (info: MultiFamilyProgram) => !!info.programUrl,
      availableResolver: (info: MultiFamilyProgram) =>
        `<a href="${info.programUrl}" class="link" target="_blank">${info.programUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noProgramUrl'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      infoType: 'pdfUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.multiFamilyProgram.pdfUrl',
      icon: 'custom-solid:RoundedContentBallot',
      infoText: 'requestForm.questions.general.pdfUrlInfo',
      availableValueFormat: 'html',
      ifInfoAvailable: (info: MultiFamilyProgram) => !!info.pdfUrl,
      availableResolver: (info: MultiFamilyProgram) =>
        `<a href="${info.pdfUrl}" class="link" target="_blank">${info.pdfUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noPdfUrl'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      infoType: 'guidelinesUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.multiFamilyProgram.guidelinesUrl',
      icon: 'global:alternate-email',
      infoText: 'requestForm.questions.general.webpageForAcceptedMaterialInfo',
      availableValueFormat: 'html',
      ifInfoAvailable: (info: MultiFamilyProgram) => !!info.guidelinesUrl,
      availableResolver: (info: MultiFamilyProgram) =>
        `<a href="${info.guidelinesUrl}" class="link" target="_blank">${info.guidelinesUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noGuidelinesUrl'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      infoType: 'numberOfProperties',
      type: 'standard',
      locations: ['community-profile'],
      availableValueFormat: 'html',
      title: 'widgets.multiFamilyProgram.numberOfProperties',
      icon: 'custom-solid:Multifamily',
      infoText: 'requestForm.questions.multiFamily.numberOfPropertiesInfo',
      ifInfoAvailable: (info: MultiFamilyProgram) =>
        (info.numberOfProperties || 0) > 0,
      availableResolver: (info: MultiFamilyProgram) =>
        decimalPipe.transform(info.numberOfProperties, '1.0-0'),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noNumberOfPropertiesInfo'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      infoType: 'numberOfPropertiesWithAccess',
      type: 'standard',
      locations: ['community-profile'],
      title: 'widgets.multiFamilyProgram.numberOfPropertiesWithAccess',
      icon: 'custom-solid:Multifamily',
      infoText:
        'requestForm.questions.multiFamily.numberOfPropertiesWithAccessInfo',
      ifInfoAvailable: (info: MultiFamilyProgram) =>
        (info.numberOfPropertiesWithAccess || 0) > 0,
      availableResolver: (info: MultiFamilyProgram) =>
        decimalPipe.transform(info.numberOfPropertiesWithAccess, '1.0-0'),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noNumberOfPropertiesWithAccessInfo'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      infoType: 'containerTypes',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.multiFamilyProgram.containerTypes',
      icon: 'custom-solid:Container',
      infoText: 'requestForm.questions.multiFamily.containerTypesInfo',
      ifInfoAvailable: (info: MultiFamilyProgram) =>
        (info.containerTypes || []).length > 0,
      availableResolver: (info: MultiFamilyProgram) =>
        containersPipe.transform(
          info.containerTypes,
          translateService.instant(
            'widgets.multiFamilyProgram.notice.noContainerInfo'
          )
        ),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noContainerInfo'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      infoType: 'collectionType',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],

      title: 'widgets.multiFamilyProgram.typeOfCollection',
      icon: 'custom-solid:CollectionFormat',
      infoText: 'requestForm.questions.multiFamily.collectionTypeInfo',
      ifInfoAvailable: (info: MultiFamilyProgram) => !!info.collectionType,
      availableResolver: (info: MultiFamilyProgram) => info.collectionType,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noCollectionTypeInfo'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      infoType: 'hasAdditionalServices',
      type: 'standard',
      locations: ['community-profile'],
      title: 'widgets.multiFamilyProgram.additionalMultifamilyServices',
      icon: 'global:AdditionalMultifamilyServices',
      infoText: 'requestForm.questions.multiFamily.additionalServicesInfo',
      ifInfoAvailable: (info: MultiFamilyProgram) =>
        utilService.doesMultiFamilyHaveAdditionalServices(info),
      availableResolver: (info: MultiFamilyProgram) =>
        additionalServicePipe.transform(
          info,
          'widgets.multiFamilyProgram.notice.noAdditionalServices'
        ),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.multiFamilyProgram.notice.noAdditionalServices'
        ),
    },
    {
      section: 'multiFamilyPrograms',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      infoType: 'ordinanceUrl',
      title: 'widgets.multiFamilyProgram.ordinanceLink',
      infoText: 'requestForm.questions.multiFamily.ordinanceLinkInfo',
      icon: 'custom-solid:OpenNew',
      availableValueFormat: 'html',
      ifInfoAvailable: (info: MultiFamilyProgram) => !!info.ordinanceUrl,
      availableResolver: (info: MultiFamilyProgram) =>
        `<a href="${info.ordinanceUrl}" class="link" target="_blank">${info.ordinanceUrl}</a>`,
      unavailableResolver: (info: MultiFamilyProgram) =>
        translateService.instant(
          info.hasOrdinance
            ? 'widgets.multiFamilyProgram.notice.noOrdinanceInfoTrue'
            : 'widgets.multiFamilyProgram.notice.noOrdinanceInfoFalse'
        ),
    },
  ],
});

export default multiFamilyPrograms;
